/**
 * useErrorHandler.js - Composable for centralized error handling
 *
 * Applying Composables Pattern from PATTERNS_FROM_BASKET_BASE.md
 * Following ADR-016: Centralized Error Handling Pattern
 *
 * This composable provides:
 * - Extraction of detailed error info from API responses
 * - Mapping of error codes to user-friendly Spanish messages
 * - Support for validation details
 * - Consistent error formatting across the application
 *
 * Legal context: Error messages must be clear and actionable for users
 * managing sensitive legal processes (GDPR, right to be forgotten).
 *
 * GDPR compliance: Error messages do not expose internal system details
 * or sensitive user data.
 */

/**
 * Map of backend error codes to user-friendly Spanish messages
 * These codes come from server/utils/apiHelpers.js
 */
const ERROR_CODE_MESSAGES = {
  // Validation & Input Errors
  VALIDATION_ERROR: 'Los datos proporcionados no son válidos',
  INVALID_INPUT: 'Los datos proporcionados no son válidos',
  MISSING_REQUIRED_FIELD: 'Faltan campos obligatorios',
  INVALID_FORMAT: 'El formato de los datos no es correcto',

  // Authentication Errors
  AUTHENTICATION_ERROR: 'Credenciales incorrectas o sesión expirada',
  INVALID_CREDENTIALS: 'El email o la contraseña son incorrectos',
  TOKEN_EXPIRED: 'Tu sesión ha expirado. Por favor, inicia sesión de nuevo',
  TOKEN_INVALID: 'Tu sesión no es válida. Por favor, inicia sesión de nuevo',
  SESSION_EXPIRED: 'Tu sesión ha expirado. Por favor, inicia sesión de nuevo',

  // Authorization Errors
  AUTHORIZATION_ERROR: 'No tienes permisos para realizar esta acción',
  FORBIDDEN: 'No tienes permisos para acceder a este recurso',
  INSUFFICIENT_PERMISSIONS: 'No tienes los permisos necesarios para esta operación',
  ACCESS_DENIED: 'Acceso denegado a este recurso',
  TIER_ACCESS_DENIED: 'Tu plan actual no incluye esta funcionalidad',

  // Resource Errors
  NOT_FOUND: 'El recurso solicitado no existe',
  RESOURCE_NOT_FOUND: 'El recurso solicitado no existe',
  USER_NOT_FOUND: 'Usuario no encontrado',
  CASE_NOT_FOUND: 'Caso no encontrado',
  DOCUMENT_NOT_FOUND: 'Documento no encontrado',
  CONTRACT_NOT_FOUND: 'No se encontró el contrato solicitado',

  // Conflict Errors
  CONFLICT: 'Ya existe un registro con estos datos',
  EMAIL_EXISTS: 'Ya existe una cuenta con este email',
  DNI_EXISTS: 'Ya existe una cuenta con este DNI',
  DUPLICATE_ENTRY: 'Este registro ya existe en el sistema',

  // Rate Limiting
  RATE_LIMIT_ERROR: 'Demasiadas solicitudes. Intenta de nuevo en unos minutos',
  TOO_MANY_REQUESTS: 'Has excedido el límite de solicitudes. Espera unos minutos',

  // Server Errors
  INTERNAL_ERROR: 'Error interno del servidor. Intenta de nuevo más tarde',
  SERVER_ERROR: 'Error del servidor. Por favor, intenta de nuevo más tarde',
  DATABASE_ERROR: 'Error de conexión. Por favor, intenta de nuevo',
  SERVICE_UNAVAILABLE: 'El servicio no está disponible temporalmente. Intenta de nuevo en unos minutos',

  // Business Logic Errors - Cases
  CASE_LIMIT_REACHED: 'Ya tienes un caso activo. Solo puedes tener un caso por cuenta',
  CASE_ALREADY_EXISTS: 'Ya tienes un caso asociado a tu cuenta',
  CASE_CLOSED: 'Este caso ha sido cerrado y no puede modificarse',
  CASE_IN_PROGRESS: 'No puedes realizar esta acción mientras el caso está en proceso',

  // Payment Errors
  PAYMENT_ERROR: 'Error al procesar el pago. Verifica tus datos de tarjeta',
  PAYMENT_REQUIRED: 'Es necesario completar el pago para continuar',
  PAYMENT_FAILED: 'El pago no pudo procesarse. Por favor, intenta con otra forma de pago',
  SUBSCRIPTION_EXPIRED: 'Tu suscripción ha expirado',

  // Documentation Errors
  CONTRACT_EXPIRED: 'El contrato ha expirado. Por favor, inicia el proceso de nuevo',
  CONTRACT_NOT_SIGNED: 'El contrato debe ser firmado para continuar',
  DOCUMENTATION_PENDING: 'Debes completar la documentación requerida',
  DOCUMENTATION_REJECTED: 'Tu documentación ha sido rechazada. Revisa los comentarios',
  INVALID_DOCUMENT_TYPE: 'El tipo de documento no es válido',
  FILE_TOO_LARGE: 'El archivo es demasiado grande. El tamaño máximo es 10MB',
  INVALID_FILE_TYPE: 'Tipo de archivo no permitido',

  // Security Errors
  CSRF_TOKEN_INVALID: 'Tu sesión ha expirado. Por favor, recarga la página',
  CSRF_TOKEN_MISSING: 'Error de seguridad. Por favor, recarga la página',
  NONCE_ALREADY_USED: 'Esta solicitud ya fue procesada anteriormente',
  HASH_MISMATCH: 'Error de verificación de integridad. Por favor, recarga la página',

  // Email Verification
  EMAIL_NOT_VERIFIED: 'Debes verificar tu email antes de continuar',
  VERIFICATION_TOKEN_EXPIRED: 'El enlace de verificación ha expirado. Solicita uno nuevo',
  VERIFICATION_TOKEN_INVALID: 'El enlace de verificación no es válido',

  // Password Reset
  RESET_TOKEN_EXPIRED: 'El enlace para restablecer la contraseña ha expirado',
  RESET_TOKEN_INVALID: 'El enlace para restablecer la contraseña no es válido',
  PASSWORD_TOO_WEAK: 'La contraseña no cumple con los requisitos de seguridad',

  // URL/Platform Errors
  URL_LIMIT_EXCEEDED: 'Has alcanzado el límite máximo de URLs (5)',
  INVALID_URL: 'La URL proporcionada no es válida'
}

/**
 * Default fallback messages by HTTP status code
 */
const STATUS_CODE_MESSAGES = {
  400: 'Los datos enviados no son válidos',
  401: 'No estás autenticado. Por favor, inicia sesión',
  403: 'No tienes permisos para realizar esta acción',
  404: 'El recurso solicitado no existe',
  409: 'Ya existe un registro con estos datos',
  422: 'Los datos proporcionados no son válidos',
  429: 'Demasiadas solicitudes. Por favor, espera un momento',
  500: 'Error interno del servidor. Intenta de nuevo más tarde',
  502: 'El servidor no está disponible temporalmente',
  503: 'El servicio no está disponible temporalmente'
}

/**
 * Extract error information from an API error response
 *
 * @param {Error|Object} err - The error object from catch block
 * @returns {Object} Extracted error info with message, errorCode, details, status
 */
export function extractError(err) {
  // Handle null/undefined
  if (!err) {
    return {
      message: 'Error desconocido',
      errorCode: null,
      details: [],
      status: null
    }
  }

  // Extract from axios response
  const response = err.response
  const data = response?.data
  const status = response?.status

  // Get error code from response
  const errorCode = data?.errorCode || data?.code || err.errorCode || null

  // Get details array (validation errors)
  let details = []
  if (data?.details && Array.isArray(data.details)) {
    details = data.details
  } else if (err.details && Array.isArray(err.details)) {
    details = err.details
  }

  // Determine the message with priority:
  // 1. Backend message (most specific)
  // 2. Error code mapped message
  // 3. Status code fallback
  // 4. Generic error message
  let message = data?.message || data?.error || err.message

  // If message is generic or missing, try to get from error code
  if (!message || message === 'Error' || message === 'Request failed') {
    if (errorCode && ERROR_CODE_MESSAGES[errorCode]) {
      message = ERROR_CODE_MESSAGES[errorCode]
    } else if (status && STATUS_CODE_MESSAGES[status]) {
      message = STATUS_CODE_MESSAGES[status]
    } else {
      message = 'Ha ocurrido un error inesperado'
    }
  }

  return {
    message,
    errorCode,
    details,
    status
  }
}

/**
 * Format error for display in UI
 * Combines message with error code context if available
 *
 * @param {Error|Object} err - The error object
 * @param {string} defaultMessage - Fallback message if no error info found
 * @returns {Object} Formatted error with message, details, code, hasDetails
 */
export function formatError(err, defaultMessage = 'Ha ocurrido un error') {
  const extracted = extractError(err)

  return {
    message: extracted.message || defaultMessage,
    details: extracted.details,
    code: extracted.errorCode,
    status: extracted.status,
    hasDetails: extracted.details.length > 0
  }
}

/**
 * Get user-friendly message for a specific error code
 *
 * @param {string} errorCode - The error code from backend
 * @param {string} fallback - Fallback message if code not found
 * @returns {string} User-friendly message in Spanish
 */
export function getErrorMessage(errorCode, fallback = 'Ha ocurrido un error') {
  return ERROR_CODE_MESSAGES[errorCode] || fallback
}

/**
 * Check if error is a specific type
 *
 * @param {Error|Object} err - The error object
 * @param {string} errorCode - The error code to check
 * @returns {boolean} True if error matches the code
 */
export function isErrorCode(err, errorCode) {
  const extracted = extractError(err)
  return extracted.errorCode === errorCode
}

/**
 * Check if error is an authentication error
 *
 * @param {Error|Object} err - The error object
 * @returns {boolean} True if authentication error
 */
export function isAuthError(err) {
  const extracted = extractError(err)
  const authCodes = [
    'AUTHENTICATION_ERROR',
    'INVALID_CREDENTIALS',
    'TOKEN_EXPIRED',
    'TOKEN_INVALID',
    'SESSION_EXPIRED'
  ]
  return extracted.status === 401 || authCodes.includes(extracted.errorCode)
}

/**
 * Check if error is a validation error
 *
 * @param {Error|Object} err - The error object
 * @returns {boolean} True if validation error
 */
export function isValidationError(err) {
  const extracted = extractError(err)
  return (
    extracted.status === 400 ||
    extracted.status === 422 ||
    extracted.errorCode === 'VALIDATION_ERROR' ||
    extracted.details.length > 0
  )
}

/**
 * Check if error is a rate limit error
 *
 * @param {Error|Object} err - The error object
 * @returns {boolean} True if rate limit error
 */
export function isRateLimitError(err) {
  const extracted = extractError(err)
  return extracted.status === 429 || extracted.errorCode === 'RATE_LIMIT_ERROR'
}

/**
 * Composable hook for error handling in components
 *
 * @returns {Object} Error handling utilities
 */
export function useErrorHandler() {
  return {
    extractError,
    formatError,
    getErrorMessage,
    isErrorCode,
    isAuthError,
    isValidationError,
    isRateLimitError,
    ERROR_CODE_MESSAGES
  }
}

export default useErrorHandler
